import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Paper, Box, Button, Chip, CircularProgress, Alert,
  TextField, Grid, Card, CardMedia, Divider, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, IconButton
} from '@mui/material';
import { ArrowBack, Add, CloudUpload, Delete as DeleteIcon } from '@mui/icons-material';

const API_URL = window.API_URL || 'http://localhost:3992/api';

export default function VistoriaAluguel() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [aluguel, setAluguel] = useState(null);
  const [vistoria, setVistoria] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    km_final: '',
    nivel_combustivel: '',
    observacoes: ''
  });
  const [criando, setCriando] = useState(false);
  const [uploadAndando, setUploadAndando] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dialogFotoOpen, setDialogFotoOpen] = useState(false);
  const [fotoAtual, setFotoAtual] = useState(null);
  const [obsAtualFoto, setObsAtualFoto] = useState('');
  const [tipoFotoAtual, setTipoFotoAtual] = useState('geral');
  const fileInputRef = React.useRef(null);

  const carregarVistoria = useCallback(async (vistoriaId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/vistorias/${vistoriaId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const vistoriaData = await res.json();
        setVistoria(vistoriaData);
      }
    } catch (err) {
      console.log('Erro ao carregar vistoria:', err.message);
    }
  }, []);

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const resAluguel = await fetch(`${API_URL}/alugueis/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!resAluguel.ok) throw new Error('Aluguel não encontrado');
      const aluguelData = await resAluguel.json();
      setAluguel(aluguelData);

      try {
        const resVistoria = await fetch(`${API_URL}/vistorias/alugueis-ativos`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (resVistoria.ok) {
          const vistoriaData = await resVistoria.json();
          const vistoriaAluguel = vistoriaData.find(v => v.id_aluguel === parseInt(id));
          if (vistoriaAluguel?.vistoria_id) {
            carregarVistoria(vistoriaAluguel.vistoria_id);
          }
        }
      } catch (err) {
        console.log('Sem vistoria ainda');
      }

      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [carregarVistoria, id]);

  // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/set-state-in-effect
  useEffect(() => {
    if (id) {
      carregarDados();
    }
  }, [id]);

  const criarVistoria = async () => {
    try {
      setCriando(true);
      const token = localStorage.getItem('token');

      const res = await fetch(`${API_URL}/vistorias`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id_aluguel: parseInt(id),
          km_final: parseInt(formData.km_final),
          nivel_combustivel: parseInt(formData.nivel_combustivel),
          observacoes: formData.observacoes,
          funcionario_nome: localStorage.getItem('usuario_nome') || 'Funcionário'
        })
      });

      if (!res.ok) throw new Error('Erro ao criar vistoria');
      const vistoriaData = await res.json();

      setVistoria({ id: vistoriaData.vistoria_id, fotos: [], checklist: [] });
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setCriando(false);
    }
  };

  const finalizarVistoria = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/vistorias/${vistoria.id}/finalizar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Erro ao finalizar');
      navigate('/vistorias');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleArquivoSelecionado = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFotoAtual(file);
      setDialogFotoOpen(true);
    }
  };

  const salvarFoto = async () => {
    if (!fotoAtual || !vistoria) return;

    try {
      setUploadAndando(true);
      setUploadProgress(0);

      const formDataFoto = new FormData();
      formDataFoto.append('foto', fotoAtual);
      formDataFoto.append('tipo_foto', tipoFotoAtual);
      formDataFoto.append('descricao', obsAtualFoto);

      const token = localStorage.getItem('token');

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setUploadProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 201) {
          carregarVistoria(vistoria.id);
          setFotoAtual(null);
          setObsAtualFoto('');
          setTipoFotoAtual('geral');
          setDialogFotoOpen(false);
          setUploadProgress(0);
          setUploadAndando(false);
          setError('');
        } else {
          throw new Error('Erro ao enviar');
        }
      });

      xhr.addEventListener('error', () => {
        setError('Erro de conexão');
        setUploadAndando(false);
        setUploadProgress(0);
      });

      xhr.open('POST', `${API_URL}/vistorias/${vistoria.id}/upload-foto`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formDataFoto);
    } catch (err) {
      setError('Erro: ' + err.message);
      setUploadAndando(false);
    }
  };

  const deletarFoto = async (fotoId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/vistorias/${vistoria.id}/fotos/${fotoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        carregarVistoria(vistoria.id);
      }
    } catch (err) {
      setError('Erro ao deletar foto');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'em_andamento': 'warning',
      'concluida': 'success',
      'cancelada': 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'em_andamento': 'Em Andamento',
      'concluida': 'Concluída',
      'cancelada': 'Cancelada'
    };
    return labels[status] || status;
  };

  if (loading) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!aluguel) return <Alert severity="error">{error || 'Aluguel não encontrado'}</Alert>;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/alugueis')} sx={{ mb: 2 }}>
        Voltar
      </Button>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* DADOS ALUGUEL */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <h2>{aluguel.nome_cliente}</h2>
        <p>
          {aluguel.marca} {aluguel.modelo} - <strong>{aluguel.placa}</strong>
        </p>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <p><strong>Retirada:</strong> {new Date(aluguel.data_retirada).toLocaleDateString('pt-BR')}</p>
          </Grid>
          <Grid item xs={12} sm={6}>
            <p><strong>Devolução Prevista:</strong> {new Date(aluguel.data_devolucao).toLocaleDateString('pt-BR')}</p>
          </Grid>
        </Grid>
      </Paper>

      {!vistoria ? (
        /* CRIAR VISTORIA */
        <Paper sx={{ p: 3, mb: 3 }}>
          <h3>Iniciar Vistoria de Devolução</h3>
          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="KM Final"
                type="number"
                value={formData.km_final}
                onChange={(e) => setFormData({ ...formData, km_final: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Combustível (%)"
                type="number"
                value={formData.nivel_combustivel}
                onChange={(e) => setFormData({ ...formData, nivel_combustivel: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Observações"
                multiline
                rows={4}
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                fullWidth
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={criarVistoria}
              disabled={criando}
            >
              {criando ? 'Criando...' : 'Iniciar Vistoria'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate(`/aluguel/${id}/vistoria-mobile`)}
            >
              Modo Mobile
            </Button>
          </Box>
        </Paper>
      ) : (
        /* VISTORIA ATIVA */
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <h3>Vistoria #{vistoria.id}</h3>
              <Chip
                label={getStatusLabel(vistoria.status)}
                color={getStatusColor(vistoria.status)}
              />
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <p><strong>KM Final:</strong> {vistoria.km_final}</p>
              </Grid>
              <Grid item xs={12} sm={6}>
                <p><strong>Combustível:</strong> {vistoria.nivel_combustivel}%</p>
              </Grid>
              {vistoria.observacoes && (
                <Grid item xs={12}>
                  <p><strong>Observações:</strong></p>
                  <p>{vistoria.observacoes}</p>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* FOTOS */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <h3>Fotos ({vistoria?.fotos?.length || 0})</h3>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {vistoria?.fotos?.map(foto => (
                <Grid item xs={12} sm={6} md={4} key={foto.id}>
                  <Card sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={foto.url}
                      alt={foto.descricao || 'Foto'}
                      sx={{ objectFit: 'cover', cursor: 'pointer' }}
                      onClick={() => window.open(foto.url, '_blank')}
                    />
                    <Box sx={{ p: 1 }}>
                      <Chip label={foto.tipo_foto} size="small" />
                      {foto.descricao && <p style={{ fontSize: '0.85em', margin: '4px 0 0 0' }}>{foto.descricao}</p>}
                    </Box>
                    {vistoria.status === 'em_andamento' && (
                      <IconButton
                        size="small"
                        color="error"
                        sx={{ position: 'absolute', top: 0, right: 0 }}
                        onClick={() => deletarFoto(foto.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>

            {vistoria.status === 'em_andamento' && (
              <Button
                variant="contained"
                startIcon={<CloudUpload />}
                onClick={() => fileInputRef.current?.click()}
                fullWidth
              >
                Upload Fotos
              </Button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={handleArquivoSelecionado}
            />
          </Paper>

          {/* CHECKLIST */}
          {vistoria.checklist && vistoria.checklist.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <h3>Checklist</h3>
              <TableContainer>
                <Table>
                  <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell><strong>Item</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vistoria.checklist.map(item => (
                      <TableRow key={item.id}>
                        <TableCell>{item.item}</TableCell>
                        <TableCell>
                          <Chip
                            label={item.verificado ? 'OK' : 'Não verificado'}
                            color={item.verificado ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {vistoria.status === 'em_andamento' && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="success"
                onClick={finalizarVistoria}
              >
                Finalizar Vistoria
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate(`/vistorias/${vistoria.id}`)}
              >
                Ver Detalhes Completos
              </Button>
            </Box>
          )}
        </>
      )}

      {/* FOTO UPLOAD MODAL - OBSERVAÇÕES */}
      <Dialog open={dialogFotoOpen} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar Foto</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {fotoAtual && (
            <img
              src={URL.createObjectURL(fotoAtual)}
              alt="Preview"
              style={{ width: '100%', maxHeight: '300px', borderRadius: '8px', objectFit: 'cover' }}
            />
          )}

          <TextField
            label="Tipo de Foto"
            select
            value={tipoFotoAtual}
            onChange={(e) => setTipoFotoAtual(e.target.value)}
            size="small"
            disabled={uploadAndando}
            SelectProps={{
              native: true
            }}
          >
            <option value="geral">Geral</option>
            <option value="frontal">Frontal</option>
            <option value="traseira">Traseira</option>
            <option value="lateral_esq">Lateral Esquerda</option>
            <option value="lateral_dir">Lateral Direita</option>
            <option value="interior">Interior</option>
            <option value="dano">Dano</option>
            <option value="km">Km</option>
            <option value="combustivel">Combustível</option>
          </TextField>

          <TextField
            label="Observações da Foto"
            multiline
            rows={3}
            placeholder="Ex: Riscos na porta, vidro trincado, etc"
            value={obsAtualFoto}
            onChange={(e) => setObsAtualFoto(e.target.value)}
            size="small"
            disabled={uploadAndando}
          />

          {uploadAndando && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, fontSize: '12px' }}>
                <span>Enviando...</span>
                <span>{Math.round(uploadProgress)}%</span>
              </Box>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogFotoOpen(false); setFotoAtual(null); }} disabled={uploadAndando}>Cancelar</Button>
          <Button onClick={salvarFoto} variant="contained" disabled={uploadAndando}>
            {uploadAndando ? `Enviando ${Math.round(uploadProgress)}%` : 'Salvar Foto'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
